import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileCheck } from "lucide-react";
import Navbar, { GoogleLoginButton } from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Apply() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [checkingApp, setCheckingApp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    discord_name: "",
    dob: "",
    timezone: "",
    weekly_hours: "",
    previous_servers: "",
    rp_experience: "",
    character_name: "",
    character_backstory: "",
    character_goals: "",
    scenario_fearrp: "",
    scenario_metagaming: "",
    why_join: "",
  });
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!user) return;
    setCheckingApp(true);
    api
      .get("/applications/mine")
      .then((res) => {
        const app = res.data.application;
        if (app && app.status !== "rejected") setExisting(app);
      })
      .catch(() => {})
      .finally(() => setCheckingApp(false));
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("You must agree to the server rules");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form });
      toast.success("Application submitted — staff will review it within 48 hours");
      navigate("/profile");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls =
    "bg-plum-800 border-sunset-amber/30 text-cream placeholder:text-muted-foreground/60 focus-visible:ring-sunset-amber";

  return (
    <div className="min-h-screen bg-plum-900" data-testid="apply-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-cream">
          Whitelist <span className="sunset-text">Application</span>
        </h1>
        <p className="mt-3 text-muted-foreground text-sm sm:text-base">
          Serious applicants only. Put effort into your backstory — low-effort apps are denied.
        </p>

        {loading || checkingApp ? (
          <div className="flex justify-center py-24" data-testid="apply-loading">
            <Loader2 className="h-8 w-8 animate-spin text-sunset-amber" />
          </div>
        ) : !user ? (
          <div className="mt-10 rounded-2xl border border-sunset-amber/30 bg-plum-700/70 p-10 text-center" data-testid="apply-login-prompt">
            <h2 className="font-display text-2xl font-bold uppercase text-cream mb-3">Sign in to apply</h2>
            <p className="text-muted-foreground text-sm mb-6">
              Applications are tied to your account so you can track your status and unlock your citizen profile.
            </p>
            <GoogleLoginButton />
          </div>
        ) : existing ? (
          <div className="mt-10 rounded-2xl border border-sunset-amber/30 bg-plum-700/70 p-10 text-center" data-testid="apply-existing-notice">
            <FileCheck className="h-10 w-10 text-sunset-amber mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold uppercase text-cream mb-3">
              {existing.status === "accepted" ? "You're already whitelisted" : "Application already submitted"}
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              {existing.status === "accepted"
                ? "Welcome to the city. Check out your citizen profile."
                : "Our staff team is reviewing your application. Hang tight."}
            </p>
            <Link to="/profile" data-testid="apply-view-status-button" className="btn-sunset rounded-full px-8 py-3 text-sm font-bold uppercase tracking-wider inline-block">
              View My Status
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 space-y-7" data-testid="application-form">
            <div className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-6 sm:p-8 space-y-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-sunset-amber">About You</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="discord" className="text-cream">Discord Username</Label>
                  <Input
                    id="discord"
                    data-testid="app-discord-input"
                    required
                    minLength={3}
                    placeholder="e.g. southside_jay"
                    value={form.discord_name}
                    onChange={set("discord_name")}
                    className={fieldCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dob" className="text-cream">Date of Birth (16+)</Label>
                  <Input
                    id="dob"
                    data-testid="app-dob-input"
                    required
                    type="date"
                    max="2010-12-31"
                    value={form.dob}
                    onChange={set("dob")}
                    className={`${fieldCls} [color-scheme:dark]`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-cream">Timezone / Region</Label>
                  <Select value={form.timezone} onValueChange={set("timezone")} required>
                    <SelectTrigger data-testid="app-timezone-select" className={fieldCls}>
                      <SelectValue placeholder="Select your region" />
                    </SelectTrigger>
                    <SelectContent className="bg-plum-800 border-sunset-amber/30 text-cream">
                      <SelectItem value="na-east" data-testid="app-timezone-na-east">North America — East</SelectItem>
                      <SelectItem value="na-west" data-testid="app-timezone-na-west">North America — West</SelectItem>
                      <SelectItem value="na-central" data-testid="app-timezone-na-central">North America — Central</SelectItem>
                      <SelectItem value="uk" data-testid="app-timezone-uk">United Kingdom</SelectItem>
                      <SelectItem value="eu" data-testid="app-timezone-eu">Europe</SelectItem>
                      <SelectItem value="oceania" data-testid="app-timezone-oceania">Australia / NZ</SelectItem>
                      <SelectItem value="other" data-testid="app-timezone-other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-cream">Hours per week in the city</Label>
                  <Select value={form.weekly_hours} onValueChange={set("weekly_hours")} required>
                    <SelectTrigger data-testid="app-hours-select" className={fieldCls}>
                      <SelectValue placeholder="Expected playtime" />
                    </SelectTrigger>
                    <SelectContent className="bg-plum-800 border-sunset-amber/30 text-cream">
                      <SelectItem value="0-5" data-testid="app-hours-0-5">0 – 5 hours</SelectItem>
                      <SelectItem value="5-10" data-testid="app-hours-5-10">5 – 10 hours</SelectItem>
                      <SelectItem value="10-20" data-testid="app-hours-10-20">10 – 20 hours</SelectItem>
                      <SelectItem value="20plus" data-testid="app-hours-20plus">20+ hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-cream">Roleplay Experience</Label>
                <Select value={form.rp_experience} onValueChange={set("rp_experience")} required>
                  <SelectTrigger data-testid="app-experience-select" className={fieldCls}>
                    <SelectValue placeholder="Select your experience level" />
                  </SelectTrigger>
                  <SelectContent className="bg-plum-800 border-sunset-amber/30 text-cream">
                    <SelectItem value="none" data-testid="app-experience-none">Brand new to RP</SelectItem>
                    <SelectItem value="some" data-testid="app-experience-some">Some experience (under 1 year)</SelectItem>
                    <SelectItem value="experienced" data-testid="app-experience-experienced">Experienced (1-3 years)</SelectItem>
                    <SelectItem value="veteran" data-testid="app-experience-veteran">Veteran (3+ years)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prevservers" className="text-cream">Previous RP Servers (optional)</Label>
                <Input
                  id="prevservers"
                  data-testid="app-previous-servers-input"
                  placeholder="e.g. NoPixel public, EchoRP, none"
                  value={form.previous_servers}
                  onChange={set("previous_servers")}
                  className={fieldCls}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-6 sm:p-8 space-y-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-sunset-amber">Your Character</h2>
              <div className="space-y-2">
                <Label htmlFor="charname" className="text-cream">Character Name</Label>
                <Input
                  id="charname"
                  data-testid="app-character-name-input"
                  required
                  minLength={3}
                  placeholder="e.g. Marcus 'Ghost' Rivera"
                  value={form.character_name}
                  onChange={set("character_name")}
                  className={fieldCls}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="chargals" className="text-cream">Character Goals & Occupation (optional)</Label>
                <Input
                  id="chargals"
                  data-testid="app-character-goals-input"
                  placeholder="e.g. Aspiring mechanic looking to open a custom shop"
                  value={form.character_goals}
                  onChange={set("character_goals")}
                  className={fieldCls}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="backstory" className="text-cream">Character Backstory (min 50 characters)</Label>
                <Textarea
                  id="backstory"
                  data-testid="app-backstory-input"
                  required
                  minLength={50}
                  rows={6}
                  placeholder="Where did they grow up? What brought them to the Southside? What do they want?"
                  value={form.character_backstory}
                  onChange={set("character_backstory")}
                  className={fieldCls}
                />
                <p className="text-xs text-muted-foreground">{form.character_backstory.length}/50 characters minimum</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="whyjoin" className="text-cream">Why do you want to join Southside? (min 30 characters)</Label>
                <Textarea
                  id="whyjoin"
                  data-testid="app-why-join-input"
                  required
                  minLength={30}
                  rows={4}
                  placeholder="What are you looking for in an RP community?"
                  value={form.why_join}
                  onChange={set("why_join")}
                  className={fieldCls}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-6 sm:p-8 space-y-6">
              <div>
                <h2 className="font-display text-xl font-bold uppercase tracking-wide text-sunset-amber">Scenario Questions</h2>
                <p className="text-xs text-muted-foreground mt-1">There are no trick answers — we want to see how you think in character.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="scenario1" className="text-cream leading-relaxed">
                  You're leaving the bank when two masked men aim pistols at you and demand your valuables.
                  You're carrying $5,000 and your phone. Walk us through exactly what you do, and why.
                </Label>
                <Textarea
                  id="scenario1"
                  data-testid="app-scenario-fearrp-input"
                  required
                  minLength={30}
                  rows={4}
                  placeholder="Describe your actions step by step..."
                  value={form.scenario_fearrp}
                  onChange={set("scenario_fearrp")}
                  className={fieldCls}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scenario2" className="text-cream leading-relaxed">
                  You get downed in a gang shootout and lose all memory of it (NLR). Minutes later, a friend who wasn't
                  in the city messages you on Discord with the shooter's name and location. What do you do with that
                  information, and why?
                </Label>
                <Textarea
                  id="scenario2"
                  data-testid="app-scenario-meta-input"
                  required
                  minLength={30}
                  rows={4}
                  placeholder="Describe what your character does next..."
                  value={form.scenario_metagaming}
                  onChange={set("scenario_metagaming")}
                  className={fieldCls}
                />
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-5">
              <Checkbox
                id="rules-agree"
                data-testid="app-rules-checkbox"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(!!v)}
                className="mt-0.5 border-sunset-amber/50 data-[state=checked]:bg-sunset-amber data-[state=checked]:text-plum-900"
              />
              <Label htmlFor="rules-agree" className="text-sm text-cream/90 leading-relaxed cursor-pointer">
                I have read the <a href="/#rules" className="text-sunset-amber underline">city rulebook</a> and agree to follow
                all server rules, including FearRP, NLR, and no metagaming.
              </Label>
            </div>

            <Button
              type="submit"
              data-testid="app-submit-button"
              disabled={submitting}
              className="btn-sunset rounded-full px-10 py-6 text-sm font-bold uppercase tracking-wider w-full sm:w-auto"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {submitting ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
